import { AwardHistory, AwardHistoryModalCellRedererType, DateRangePresetConfig, FullHistoryItems } from '../../../models/manual-award.model';
import { Component, Inject, LOCALE_ID, OnInit } from '@angular/core';
import { CvAwardHistoryModalCellRendererComponent } from '../cv-award-history-modal-cell-renderer/cv-award-history-modal-cell-renderer.component'
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';;
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { CvNumberLocalizePipe } from 'src/app/reusables/pipes/cv-number-localize.pipe';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { formatDate } from '@angular/common';
import { GridApi, GridOptions } from 'ag-grid-community';
import { ManualAwardsService } from '../../../services/manual-awards.service';
import { ManualAward } from '../../../models/manual-award.model';
import moment from 'moment';
import { end } from '@popperjs/core';


@Component({
    selector: 'cv-award-history-modal',
    templateUrl: './cv-award-history-modal.component.html',
    styleUrl: './cv-award-history-modal.component.scss'
})
export class CvAwardHistoryModalComponent implements OnInit {

    gridOptions: GridOptions = null;
    awardHistoryList: FullHistoryItems[] = [];
    gridApi: GridApi;

    dateRange: { start: Date; end: Date };
    maxDate: Date = moment().endOf('day').toDate();
    minDate: Date = moment().subtract(1, 'year').toDate();
    dateFormat: string = 'MMM d, y';
    datePresets: (DateRangePresetConfig | string)[] = [];
    gridDateFormat = 'MMM d, y hh:mm a';
    filter = {
        searchTerm: ""
    };
    public isLoading: boolean = false;

    constructor(
        @Inject(LOCALE_ID) private locale: string,
        private cvLocalizationService: CVLocalizationService,
        private dialogRef: DynamicDialogRef,
        private manualAwardsService: ManualAwardsService,
        private cvNumberLocalizePipe: CvNumberLocalizePipe,
        private cvConfigurationService: CvConfigurationService,
        
    ) {
        this.initializeDateRange();
     }

    get headerTitle() {
        return this.getLocalizationString("Awards_History_Title", "Awards History");
    }

    get closeLabel() {
        return this.getLocalizationString("Close_Button", "Close");
    }

    get datePresetsForPicker(): any {
        return this.datePresets as any;
    }

    async ngOnInit() {
        await this.loadLocalization();
        this.initializeDateRangePresets();
        this.setupGrid();
        await this.loadAwardHistory();
        
    }

    async loadAwardHistory(): Promise<void> {
        this.isLoading = true;
        try {
            // Force local timezone interpretation by avoiding UTC conversion
            const startTime = Date.UTC(
                this.dateRange.start.getFullYear(),
                this.dateRange.start.getMonth(),
                this.dateRange.start.getDate(),
                0, 0, 0, 0
            );

            const endTime = Date.UTC(
                this.dateRange.end.getFullYear(),
                this.dateRange.end.getMonth(),
                this.dateRange.end.getDate(),
                23, 59, 59, 999
            );

            const awardHistory: AwardHistory = await this.manualAwardsService.loadAllAwardHistory(
                startTime,
                endTime
            );

            this.awardHistoryList = awardHistory.FullHistoryItems?.sort((a, b) =>
                new Date(b.Date).getTime() - new Date(a.Date).getTime()
            ) || [];

            if(this.gridApi) {
                this.gridApi.setRowData(this.awardHistoryList);
                this.manageNoRowsOverlay();
            }

        } catch (error) {
            console.error('Failed to load the award history', error);
            this.awardHistoryList = [];
            if (this.gridApi) {
                this.gridApi.setRowData([]);
                this.manageNoRowsOverlay();
            }
        } finally {
            this.isLoading = false;
        }
    }

    initializeDateRange(): void {
        const endDate = new Date();
        const startDate = moment().subtract(30, 'days').toDate();

        this.dateRange = {
            start: startDate,
            end: endDate
        };
    }

    private initializeDateRangePresets(): void {
        this.datePresets = [
            {
                id: 'yesterday',
                label: this.getLocalizationString('Yesterday', 'Yesterday'),
                type: 'relative',
                unit: 'day',
                unitValue: -1,
                includeToday: false,
            },
            {
                id: 'this-week',
                label: this.getLocalizationString('This_Week', 'This Week'),
                type: 'custom',
                fn: () => {
                    const startOfWeek = moment().startOf('week');
                    const today = moment().endOf('day');
                    return {
                        start: startOfWeek.toDate(),
                        end: today.toDate()
                    };
                }
            },

            {
                id: 'last-week',
                label: this.getLocalizationString('Last_Week', 'Last Week'),
                type: 'relative',
                unit: 'week',
                unitValue: -1,
                includeToday: true,
            },
            {
                id: 'this-month',
                label: this.getLocalizationString('This_Month', 'This Month'),
                type: 'custom',
                fn: () => {
                    const startOfMonth = moment().startOf('month');
                    const now = moment();
                    return {
                        start: startOfMonth.toDate(),
                        end: now.toDate()
                    };
                }
            },
            {
                id: 'last-month',
                label: this.getLocalizationString('Last_Month', 'Last Month'),
                type: 'relative',
                unit: 'month',
                unitValue: -1,
                includeToday: true,
            },
            {
                id: 'this-quarter',
                label: this.getLocalizationString('This_Quarter', 'This Quarter'),
                type: 'custom',
                fn: () => {
                    const startOfQuarter = moment().startOf('quarter');
                    const today = moment().endOf('day');
                    return {
                        start: startOfQuarter.toDate(),
                        end: today.toDate()
                    };
                }
            },
            {
                id: 'last-quarter',
                label: this.getLocalizationString('Last_Quarter', 'Last Quarter'),
                type: 'custom',
                fn: () => {
                    const lastQuarterStart = moment().subtract(1, 'quarter').startOf('quarter');
                    const lastQuarterEnd = moment().subtract(1, 'quarter').endOf('quarter');
                    return {
                        start: lastQuarterStart.toDate(),
                        end: lastQuarterEnd.toDate()
                    };
                }
            },

        ];
    }

    onDateRangeChange(newDateRange: { start: Date; end: Date }): void {
        if (newDateRange && newDateRange.start && newDateRange.end) {
            this.dateRange = newDateRange;
            this.loadAwardHistory();
        }
    }

    shouldShowNoHistory(): boolean {
        return !this.isLoading &&
            this.awardHistoryList.length === 0 &&
            (!this.filter.searchTerm || this.filter.searchTerm.trim() === '');
    }

    manageNoRowsOverlay() {
        if (!this.gridApi) {
            return;
        }
        if (this.gridApi.getDisplayedRowCount() === 0) {
            this.gridApi.showNoRowsOverlay();
        }
        else {
            this.gridApi.hideOverlay();
        }
    }

    setupGrid() {
        this.gridOptions = {
            suppressDragLeaveHidesColumns: true,
            suppressRowGroupHidesColumns: true,
            suppressContextMenu: true,
            suppressCellFocus: true,
            rowHeight: 48,
            headerHeight: 44,
            suppressPreventDefaultOnMouseWheel: true,
            suppressHorizontalScroll: true,
            enableBrowserTooltips: false,
            suppressMovableColumns: true,
            overlayNoRowsTemplate: `
            <div class="error-container" style="padding-top: 48px;">
                <svg class="mb-2" style="width: 240px; height: 150px;">
                    <use href="../App_Themes/CXone/icons/no-results-big.svg#no-results-big"></use>
                </svg>
                <p class="cxone-empty-text">
                    ${this.getLocalizationString("No_Results_Found", "No results found")}
                </p>
            </div>`,
            rowData: [],
            //Using a custom filter to handle search term
            isExternalFilterPresent: () => this.filter.searchTerm?.trim().length > 0,
            doesExternalFilterPass: (node) => {
                if (!this.filter.searchTerm?.trim()) {
                    return true;
                }
                const searchTerm = this.filter.searchTerm.toLowerCase();
                const matchesBadgeName = node.data.Name.toLowerCase().includes(searchTerm);
                const matchesRecipient = node.data.RecipientName.toLowerCase().includes(searchTerm);
                const matchesAwarder = node.data.AwarderName.toLowerCase().includes(searchTerm);
                return matchesBadgeName || matchesRecipient || matchesAwarder;
        },
            columnDefs: [
                {
                    headerName: this.getLocalizationString("Award", "Award"),
                    field: 'Name',
                    sortable: false,
                    headerClass: 'history-modal__grid-header',
                    cellRendererSelector: (params) => ({
                        component: CvAwardHistoryModalCellRendererComponent,
                        params: {
                            customClass: 'd-flex align-items-center h-100',
                            primaryText: params.data.Name,
                            columnType: AwardHistoryModalCellRedererType.AWARD,
                        }
                    })
                },
                {
                    headerName: this.getLocalizationString("Coins", "Coins"),
                    field: 'CoinAmt',
                    sortable: false,
                    headerClass: 'history-modal__grid-header',
                    cellRendererSelector: (params) => ({
                        component: CvAwardHistoryModalCellRendererComponent,
                        params: {
                            customClass: 'd-flex align-items-center h-100',
                            primaryText: typeof params.data.CoinAmt === 'number'
                                ? params.data.CoinAmt.toLocaleString(CVCultureName)
                                : params.data.CoinAmt,
                            columnType: AwardHistoryModalCellRedererType.COINS,
                        }
                    })
                },
                {
                    headerName: this.getLocalizationString("Recipient", "Recipient"),
                    field: 'RecipientName',
                    sortable: false,
                    headerClass: 'history-modal__grid-header',
                    cellRendererSelector: (params) => ({
                        component: CvAwardHistoryModalCellRendererComponent,
                        params: {
                            customClass: 'd-flex flex-column justify-content-center h-100',
                            primaryText: params.data.RecipientName,
                            columnType: AwardHistoryModalCellRedererType.DEFAULT
                        }
                    })
                },
                {
                    headerName: this.getLocalizationString("Awarded_By", "Awarded By"),
                    field: 'AwarderName',
                    sortable: false,
                    headerClass: 'history-modal__grid-header',
                    cellRendererSelector: (params) => ({
                        component: CvAwardHistoryModalCellRendererComponent,
                        params: {
                            customClass: 'd-flex flex-column justify-content-center h-100',
                            primaryText: params.data.AwarderName,
                            columnType: AwardHistoryModalCellRedererType.DEFAULT
                        }
                    })
                },
                {
                    headerName: this.getLocalizationString("Date", "Date"),
                    field: 'Date',
                    sortable: false,
                    headerClass: 'history-modal__grid-header',
                    cellRendererSelector: (params) => ({
                        component: CvAwardHistoryModalCellRendererComponent,
                        params: {
                            customClass: 'd-flex flex-column justify-content-center h-100',
                            primaryText: formatDate(params.data?.Date ?? '', this.gridDateFormat, this.locale),
                            columnType: AwardHistoryModalCellRedererType.DEFAULT
                        }
                    })
                }
            ],
            defaultColDef: {
                resizable: false,
                suppressMenu: true,
            },
            onGridSizeChanged: (event) => {
                event.api.sizeColumnsToFit();
            },
            onGridReady: (event) => {
                this.gridApi = event.api;
                event.api.sizeColumnsToFit();
                this.manageNoRowsOverlay();
            },
        };
    }

    async loadLocalization() {
        await this.cvLocalizationService.GetSection("Award_History_Modal");
    }

    getLocalizationString(key: string, defaultValue: string) {
        return this.cvLocalizationService.getCachedText('Award_History_Modal', key, defaultValue);
    }

    onDismiss() {
        this.dialogRef.close();
    }

    onClose() {
        this.dialogRef.close();
    }

    get awardHistoryListCount() {
        if (this.filter.searchTerm?.trim()) {
            // Count filtered rows from ag-Grid
            let filteredCount = 0;
            this.gridApi?.forEachNodeAfterFilter(() => filteredCount++);
            return filteredCount;
        }
        return this.awardHistoryList.length;
    }

    getCombinedHistoryLabel() {
        const localizedCount = this.cvNumberLocalizePipe.transform(this.awardHistoryListCount);
        const label = this.awardHistoryListCount === 1 ? this.getLocalizationString("Award", "award") : this.getLocalizationString("Awards", "awards")
        return `${localizedCount} ${label}`;
    }
    
    applyFilter(): void {
        if (this.gridApi) {
            this.gridApi.onFilterChanged();
            this.manageNoRowsOverlay();
        }
    }

    onSearchChange(searchTerm: string): void {
        this.filter.searchTerm = searchTerm;
        this.applyFilter();
    }
}
